import React, { useState } from 'react';
import { 
  Phone, 
  Clock, 
  User, 
  FileText, 
  CheckCircle,
  X,
  Calendar,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  DollarSign,
  TrendingUp
} from 'lucide-react';
import { CallRequest } from '../../types';
import { useDoctorData } from '../../context/DoctorDataContext';
import { apiService, handleApiError } from '../../services/api';
import { formatTimeWithTimezone, formatMeetingTime } from '../../utils/timezone';
import { useToast } from '../common/ToastContainer';
import LoadingOverlay from '../common/LoadingOverlay';
import AcceptRequestModal from './AcceptRequestModal';
import RequestAttachments from './RequestAttachments';
import PatientConsultationHistory from './PatientConsultationHistory';

// Simple date formatting function (fallback for non-timezone dates)
const formatDate = (dateString: string, formatType: 'full' | 'time' = 'full') => {
  const date = new Date(dateString);
  if (formatType === 'time') {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

interface CompletedRequest {
  id: string;
  patientName: string;
  patientEmail: string;
  status: 'completed';
  callDuration: number | null;
  consultationFee: string;
  doctorEarnings: string;
  paymentStatus: 'unpaid' | 'pending' | 'paid' | 'failed' | 'refunded';
  scheduledMeetingDateTime: string;
  completedAt: string;
  symptoms: string[];
  timezone?: string;
}

interface DoctorCallRequestsProps {
  doctorId: string;
}

const DoctorCallRequests: React.FC<DoctorCallRequestsProps> = ({ doctorId }) => {
  const toast = useToast();
  
  // Get data from context
  const { 
    activeRequests,
    pendingCompletionRequests,
    completedRequests: completedRequestsFromContext,
    doctorTimezone,
    loading,
    refreshing,
    error: contextError,
    refresh,
    stats
  } = useDoctorData();
  
  // Cast to proper types
  const requests = activeRequests as CallRequest[];
  const pendingNotesRequests = pendingCompletionRequests as CallRequest[];
  const completedRequests = completedRequestsFromContext as unknown as CompletedRequest[];
  
  const [activeTab, setActiveTab] = useState<'upcoming' | 'pending-notes' | 'completed'>('upcoming');
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<CallRequest | null>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false); // ✅ NEW: Accept modal state
  const [callNotes, setCallNotes] = useState(''); // Internal doctor notes
  const [patientRecommendation, setPatientRecommendation] = useState(''); // Patient-facing recommendations
  const [callDuration, setCallDuration] = useState(15);
  const [mcIssued, setMcIssued] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState<string>('all'); // 'all', 'today', 'week', 'month'
  const [showRefreshSuccess, setShowRefreshSuccess] = useState(false);

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod';

  // Helper to handle auth errors
  const handleAuthError = (response: Response) => {
    if (response.status === 401 || response.status === 403) {
      console.error('Authentication failed');
      toast.error('Session expired', 'Please log in again');
      localStorage.clear();
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      return true;
    }
    return false;
  };

  // Handle refresh with success message
  const handleRefresh = async () => {
    await refresh();
    setShowRefreshSuccess(true);
    setTimeout(() => setShowRefreshSuccess(false), 2000);
  };

  // Combine context error with local error
  React.useEffect(() => {
    if (contextError) {
      setError(contextError);
    }
  }, [contextError]);

  // Filter requests by date
  const getFilteredRequests = () => {
    // Choose the right list based on active tab
    const sourceList = activeTab === 'upcoming' ? requests : 
                       activeTab === 'pending-notes' ? pendingNotesRequests : 
                       requests;
    
    if (dateFilter === 'all') return sourceList;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    
    return sourceList.filter((request: any) => {
      const meetingDate = new Date(request.requestedDateTime || request.scheduledMeetingDateTime);
      const meetingDay = new Date(meetingDate.getFullYear(), meetingDate.getMonth(), meetingDate.getDate());
      
      if (dateFilter === 'today') {
        return meetingDay.getTime() === today.getTime();
      } else if (dateFilter === 'tomorrow') {
        return meetingDay.getTime() === tomorrow.getTime();
      } else if (dateFilter === 'week') {
        return meetingDay >= today && meetingDay < nextWeek;
      }
      return true;
    });
  };

  const filteredRequests = getFilteredRequests();

  // Filter completed requests
  const getFilteredCompletedRequests = () => {
    if (dateFilter === 'all') return completedRequests;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return completedRequests.filter((request) => {
      const completedDate = new Date(request.completedAt || request.scheduledMeetingDateTime);
      const completedDay = new Date(completedDate.getFullYear(), completedDate.getMonth(), completedDate.getDate());
      
      if (dateFilter === 'today') {
        return completedDay.getTime() === today.getTime();
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return completedDay >= weekAgo;
      } else if (dateFilter === 'month') {
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return completedDay >= monthAgo;
      }
      return true;
    });
  };

  const filteredCompletedRequests = getFilteredCompletedRequests();

  // Calculate earnings
  const filteredEarnings = filteredCompletedRequests.reduce((sum, req) => {
    return sum + parseFloat(req.doctorEarnings || '0');
  }, 0);

  const getPaymentStatusBadge = (status: string) => {
    const colors = {
      'paid': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'unpaid': 'bg-gray-100 text-gray-800',
      'failed': 'bg-red-100 text-red-800',
      'refunded': 'bg-purple-100 text-purple-800'
    };
    
    return colors[status as keyof typeof colors] || colors.unpaid;
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      pending: 'status-badge status-pending',
      'in_progress': 'status-badge status-in-progress',
      completed: 'status-badge status-completed',
      cancelled: 'status-badge status-cancelled'
    };
    
    return statusClasses[status as keyof typeof statusClasses] || 'status-badge';
  };

  // Urgency badge component
  const getUrgencyBadge = (urgency: string) => {
    const colors = {
      'very-urgent': 'bg-red-100 text-red-800',
      'urgent': 'bg-yellow-100 text-yellow-800',
      'routine': 'bg-green-100 text-green-800'
    };
    
    const icons = {
      'very-urgent': <AlertTriangle className="w-3 h-3" />,
      'urgent': <Clock className="w-3 h-3" />,
      'routine': <CheckCircle className="w-3 h-3" />
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${colors[urgency as keyof typeof colors] || colors.routine}`}>
        {icons[urgency as keyof typeof icons] || icons.routine}
        {urgency?.replace('-', ' ').toUpperCase() || 'ROUTINE'}
      </span>
    );
  };

  // ✅ NEW: Open accept modal
  const handleAcceptClick = (request: CallRequest) => {
    setSelectedRequest(request);
    setShowAcceptModal(true);
  };

  // ✅ NEW: Handle accept success
  const handleAcceptSuccess = async () => {
    setShowAcceptModal(false);
    setSelectedRequest(null);
    // Don't refresh immediately - let the success toast show and keep UI stable
    // The request will naturally move to "My Active Requests" on next refresh
  };

  const handleCompleteCall = async () => {
    if (!selectedRequest) return;
    
    try {
      setActionLoading(true);
      const authToken = localStorage.getItem('authToken');
      
      // Use the correct doctor ID - same as what's used for fetching requests
      const correctDoctorId = doctorId; // This is already set correctly in the component props
      
      // Use requestId if available, otherwise fall back to id
      const actualRequestId = (selectedRequest as any).requestId || selectedRequest.id;
      
      console.log('🔍 Completing request with ID:', actualRequestId);
      
      const response = await fetch(`${API_BASE_URL}/call-requests/${actualRequestId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          doctorId: correctDoctorId, // Use the correct doctor ID
          callDuration,
          callNotes, // Internal doctor notes (private)
          patientRecommendation, // Patient-facing recommendations (sent via email)
          medicalCertificateIssued: mcIssued
        })
      });

      if (handleAuthError(response)) return; // Redirect to login if auth failed

      if (response.ok) {
        toast.success('Consultation completed!', 'Patient will receive your recommendations via email');
        setShowCompleteModal(false);
        setSelectedRequest(null);
        setCallNotes('');
        setPatientRecommendation('');
        setCallDuration(15);
        setMcIssued(false);
        refresh();
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to complete call' }));
        throw new Error(errorData.error || 'Failed to complete call');
      }
    } catch (err) {
      console.error('Failed to complete call:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to complete call', errorMessage);
      setError(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelCall = async () => {
    if (!selectedRequest) return;
    
    try {
      setActionLoading(true);
      
      // Use requestId if available, otherwise fall back to id
      const actualRequestId = (selectedRequest as any).requestId || selectedRequest.id;
      
      console.log('🔍 Cancelling request with ID:', actualRequestId);
      
      await apiService.doctors.cancelCall(actualRequestId, cancelReason);
      
      // Reset form and close modal
      setShowCancelModal(false);
      setSelectedRequest(null);
      setCancelReason('');
      
      // Refresh the requests list
      await refresh();
    } catch (err) {
      console.error('Failed to cancel call:', err);
      setError(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const openCompleteModal = (request: CallRequest) => {
    setSelectedRequest(request);
    setShowCompleteModal(true);
  };

  const openCancelModal = (request: CallRequest) => {
    setSelectedRequest(request);
    setShowCancelModal(true);
  };

  if (loading) {
    return (
      <div className="relative min-h-[400px]">
        <LoadingOverlay message="Loading your requests..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-luma-text">My Requests</h1>
          
          {/* Success message */}
          {showRefreshSuccess && (
            <div className="flex items-center space-x-2 text-green-600 animate-fade-in">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Updated!</span>
            </div>
          )}
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-1 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-6 py-3 font-medium text-sm transition-colors relative ${
              activeTab === 'upcoming'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Upcoming ({requests.length})
          </button>
          <button
            onClick={() => setActiveTab('pending-notes')}
            className={`px-6 py-3 font-medium text-sm transition-colors relative ${
              activeTab === 'pending-notes'
                ? 'text-orange-600 border-b-2 border-orange-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Pending Notes ({pendingNotesRequests.length})
            {pendingNotesRequests.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-orange-500 rounded-full">
                !
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`px-6 py-3 font-medium text-sm transition-colors relative ${
              activeTab === 'completed'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Completed ({completedRequests.length})
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800 text-sm mt-2"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Upcoming Tab Content */}
      {activeTab === 'upcoming' && (
        <>
          {/* Header Controls */}
          <div className="flex items-center justify-between">
            <p className="text-gray-600">Your accepted consultations sorted by nearest time</p>
            
            <div className="flex space-x-2">
              {/* Date Filter */}
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent"
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="tomorrow">Tomorrow</option>
                <option value="week">This Week</option>
              </select>
              
              <button
                type="button" onClick={(e) => { e.preventDefault(); handleRefresh(); }}
                disabled={loading || refreshing}
                className="btn-secondary flex items-center space-x-2"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>

          {/* Requests Table */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luma-primary"></div>
            </div>
          ) : filteredRequests.length === 0 ? (
        <div className="card text-center py-12">
          <Phone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No upcoming consultations</h3>
          <p className="text-gray-500">
            {dateFilter !== 'all' ? 'No consultations for the selected date range.' : 'Accepted consultations will appear here.'}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Meeting Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Symptoms
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((request: any) => {
                  const canJoinNow = request.canJoinNow || false;
                  const minutesUntil = request.minutesUntilMeeting || 0;
                  
                  return (
                    <tr 
                      key={request.id}
                      className={`hover:bg-gray-50 ${canJoinNow ? 'bg-green-50' : ''}`}
                    >
                      {/* Status Column */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {canJoinNow && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-red-500 text-white animate-pulse">
                            🔴 Join Now
                          </span>
                        )}
                        {!canJoinNow && minutesUntil > 0 && (
                          <span className="text-xs text-gray-600">
                            {minutesUntil > 60 ? `${Math.round(minutesUntil / 60)}h` : `${Math.round(minutesUntil)}m`}
                          </span>
                        )}
                        {!canJoinNow && minutesUntil <= 0 && (
                          <span className="text-xs text-orange-600">
                            Overdue
                          </span>
                        )}
                      </td>

                      {/* Patient Column */}
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div>
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-gray-900">{request.patientName}</div>
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-mono rounded">
                                #{request.id || request.requestId}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500">
                              {request.age} • {request.gender}
                            </div>
                            {request.needsMedicalCertificate && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 mt-1">
                                <FileText className="w-3 h-3 mr-1" />
                                MC Required
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Meeting Time Column */}
                      <td className="px-4 py-4">
                        <div className="text-sm">
                          {(() => {
                            // For accepted requests, use scheduledMeetingDateTime (doctor's accepted time)
                            // For pending requests, use requestedDateTime (patient's requested time)
                            const meetingDateTime = request.scheduledMeetingDateTime || request.requestedDateTime;
                            if (!meetingDateTime) {
                              return <div className="text-gray-400">Time not specified</div>;
                            }
                            
                            const { date, time, timezone: tzLabel } = formatMeetingTime(meetingDateTime, doctorTimezone);
                            
                            return (
                              <>
                                {/* Date */}
                                <div className="text-xs text-gray-600 font-medium">{date}</div>
                                
                                {/* Time - LARGE FONT EMPHASIS */}
                                <div className="text-lg font-bold text-blue-600">{time}</div>
                                
                                {/* Timezone label - ALWAYS SHOWN */}
                                <div className="text-xs text-gray-500">({tzLabel})</div>
                                
                                {/* Duration */}
                                <div className="text-xs text-gray-500 mt-1">
                                  {request.meetingDuration || 30} min
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </td>

                      {/* Symptoms Column */}
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {request.symptoms && request.symptoms.slice(0, 3).map((symptom: string, index: number) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                            >
                              {symptom}
                            </span>
                          ))}
                          {request.symptoms && request.symptoms.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              +{request.symptoms.length - 3}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Details Column */}
                      <td className="px-4 py-4">
                        <button
                          onClick={() => setSelectedRequest(request)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View Details
                        </button>
                      </td>

                      {/* Actions Column */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div className="flex flex-col space-y-2">
                          {request.googleMeetLink && (
                            <a
                              href={request.googleMeetLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                canJoinNow
                                  ? 'bg-green-600 text-white hover:bg-green-700'
                                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              }`}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              {canJoinNow ? 'Join' : 'Link'}
                            </a>
                          )}
                          <button
                            onClick={() => openCompleteModal(request)}
                            disabled={actionLoading}
                            className="inline-flex items-center justify-center px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition-all"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Complete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
        </>
      )}

      {/* Pending Notes Tab Content */}
      {activeTab === 'pending-notes' && (
        <>
          {/* Warning Banner */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-orange-900 mb-1">Action Required: Complete Medical Notes</h3>
                <p className="text-sm text-orange-800">
                  These consultations have passed their scheduled time but haven't been marked as complete. 
                  Please add your medical notes and mark them as complete to receive your earnings.
                </p>
              </div>
            </div>
          </div>

          {/* Header Controls */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600">Consultations awaiting completion</p>
            
            <button
              type="button" onClick={(e) => { e.preventDefault(); handleRefresh(); }}
              disabled={loading || refreshing}
              className="btn-secondary flex items-center space-x-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>

          {/* Pending Notes List */}
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">All caught up!</p>
              <p className="text-sm text-gray-500 mt-1">No pending medical notes</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRequests.map((request) => (
                <div key={request.id} className="card border-l-4 border-orange-500 bg-orange-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {request.patientName || 'Anonymous Patient'}
                        </h3>
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded">
                          #{request.id || (request as any).requestId}
                        </span>
                        <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
                          Needs Completion
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                        <div>
                          <p className="text-gray-600">Scheduled Time</p>
                          <p className="font-medium text-gray-900">
                            {formatDate(request.preferredCallTime)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Symptoms</p>
                          <p className="font-medium text-gray-900">
                            {request.symptoms.join(', ')}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => {
                        setSelectedRequest(request);
                        setShowCompleteModal(true);
                      }}
                      className="btn-primary flex items-center space-x-2"
                    >
                      <FileText className="w-4 h-4" />
                      <span>Complete Now</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Completed Tab Content */}
      {activeTab === 'completed' && (
        <>
          {/* Header Controls */}
          <div className="flex items-center justify-between">
            <p className="text-gray-600">View your earnings and consultation history</p>
            
            <div className="flex space-x-2">
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
              
              <button
                type="button" onClick={(e) => { e.preventDefault(); handleRefresh(); }}
                disabled={loading || refreshing}
                className="btn-secondary flex items-center space-x-2"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>

          {/* Earnings Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Total Earnings</p>
                  <p className="text-3xl font-bold text-green-600">${filteredEarnings.toFixed(2)}</p>
                </div>
                <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Consultations</p>
                  <p className="text-3xl font-bold text-luma-text">{filteredCompletedRequests.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Avg per Consultation</p>
                  <p className="text-3xl font-bold text-luma-text">
                    ${filteredCompletedRequests.length > 0 ? (filteredEarnings / filteredCompletedRequests.length).toFixed(2) : '0.00'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Consultations List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luma-primary"></div>
            </div>
          ) : filteredCompletedRequests.length === 0 ? (
            <div className="card text-center py-12">
              <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No completed consultations</h3>
              <p className="text-gray-500">
                {dateFilter !== 'all' 
                  ? 'Try selecting a different time period' 
                  : 'Completed consultations will appear here'}
              </p>
            </div>
          ) : (
            <div className="card">
              <h2 className="text-lg font-semibold text-luma-text mb-4">Consultation History</h2>
              <div className="space-y-4">
                {filteredCompletedRequests.map((request) => (
                  <div 
                    key={request.id}
                    className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow bg-white"
                  >
                    <div className="flex items-start justify-between">
                      {/* Left: Patient Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-gray-900">{request.patientName}</h3>
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-mono rounded">
                                #{request.id}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">{request.patientEmail}</p>
                          </div>
                        </div>

                        {/* Symptoms */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {request.symptoms && request.symptoms.slice(0, 3).map((symptom, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                            >
                              {symptom}
                            </span>
                          ))}
                          {request.symptoms && request.symptoms.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              +{request.symptoms.length - 3}
                            </span>
                          )}
                        </div>

                        {/* Date and Duration */}
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{(() => {
                              const meetingDateTime = request.scheduledMeetingDateTime;
                              if (!meetingDateTime) return 'N/A';
                              const { date, time } = formatMeetingTime(meetingDateTime, doctorTimezone);
                              return `${date} at ${time}`;
                            })()}</span>
                          </div>
                          {request.callDuration && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>{request.callDuration} minutes</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right: Earnings */}
                      <div className="text-right">
                        <div className="mb-2">
                          <p className="text-sm text-gray-500">Consultation Fee</p>
                          <p className="text-lg font-medium text-gray-700">${request.consultationFee}</p>
                        </div>
                        <div className="mb-3">
                          <p className="text-sm text-gray-500">Your Earnings</p>
                          <p className="text-2xl font-bold text-green-600">
                            💰 ${request.doctorEarnings}
                          </p>
                        </div>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getPaymentStatusBadge(request.paymentStatus)}`}>
                          {request.paymentStatus === 'paid' && '✓ '}
                          {request.paymentStatus.charAt(0).toUpperCase() + request.paymentStatus.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info Note */}
          <div className="card bg-blue-50 border-blue-200">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-4 h-4 text-white" />
              </div>
              <div className="text-sm text-gray-700">
                <p className="font-medium mb-1">About Your Earnings</p>
                <p>You earn 70% of each consultation fee. The platform retains 30% to cover payment processing, infrastructure, and support services.</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Patient Details Modal */}
      {selectedRequest && !showCompleteModal && !showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-semibold text-luma-text">
                  Patient Details - {selectedRequest.patientName}
                </h3>
                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm font-mono rounded">
                  #{selectedRequest.id || (selectedRequest as any).requestId}
                </span>
              </div>
              <button
                onClick={() => setSelectedRequest(null)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Meeting Info */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">📅 Meeting Information</h4>
                <div className="space-y-1 text-sm">
                  {(() => {
                    const meetingDateTime = (selectedRequest as any).requestedDateTime || (selectedRequest as any).scheduledMeetingDateTime;
                    if (!meetingDateTime) {
                      return <p className="text-gray-500">Time not specified</p>;
                    }
                    
                    const doctorTime = formatMeetingTime(meetingDateTime, doctorTimezone);
                    const patientTimezone = (selectedRequest as any).timezone;
                    
                    return (
                      <>
                        <div>
                          <strong>Your Time:</strong>
                          <div className="text-2xl font-bold text-blue-600 my-1">{doctorTime.time}</div>
                          <div className="text-xs text-gray-600">{doctorTime.date} ({doctorTime.timezone})</div>
                        </div>
                      </>
                    );
                  })()}
                  <p className="pt-2"><strong>Duration:</strong> {(selectedRequest as any).meetingDuration || 30} minutes</p>
                  {(selectedRequest as any).googleMeetLink && (
                    <p>
                      <strong>Google Meet:</strong>{' '}
                      <a
                        href={(selectedRequest as any).googleMeetLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Join Meeting
                      </a>
                    </p>
                  )}
                </div>
              </div>

              {/* Patient Info */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">👤 Patient Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Age</p>
                    <p className="font-medium">{selectedRequest.age}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Gender</p>
                    <p className="font-medium">{selectedRequest.gender}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Email</p>
                    <p className="font-medium">{selectedRequest.patientEmail || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Phone</p>
                    <p className="font-medium">{selectedRequest.patientPhone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Language</p>
                    <p className="font-medium">{(selectedRequest as any).language || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Symptoms */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">🩺 Symptoms</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedRequest.symptoms.map((symptom, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {symptom}
                    </span>
                  ))}
                </div>
              </div>

              {/* Medical History */}
              {selectedRequest.medicalHistory && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">📋 Medical History</h4>
                  <p className="text-sm text-gray-700">{selectedRequest.medicalHistory}</p>
                </div>
              )}

              {/* Current Medications */}
              {selectedRequest.currentMedications && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">💊 Current Medications</h4>
                  <p className="text-sm text-gray-700">{selectedRequest.currentMedications}</p>
                </div>
              )}

              {/* Additional Notes */}
              {selectedRequest.additionalNotes && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">📝 Additional Notes</h4>
                  <p className="text-sm text-gray-700">{selectedRequest.additionalNotes}</p>
                </div>
              )}

              {/* Consultation Notes (Only for completed consultations) */}
              {selectedRequest.status === 'completed' && (
                <>
                  {/* Internal Doctor Notes */}
                  {(selectedRequest as any).callNotes && (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">🔒</span>
                        <h4 className="font-semibold text-gray-900">Internal Doctor Notes (Private)</h4>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{(selectedRequest as any).callNotes}</p>
                      <p className="text-xs text-gray-500 mt-2">🔒 These notes are private and not visible to the patient</p>
                    </div>
                  )}

                  {/* Patient Recommendation */}
                  {(selectedRequest as any).patientRecommendation && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">📧</span>
                        <h4 className="font-semibold text-blue-900">Patient Recommendation</h4>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{(selectedRequest as any).patientRecommendation}</p>
                      <p className="text-xs text-blue-600 mt-2">📧 This was sent to the patient via email</p>
                    </div>
                  )}

                  {/* Call Duration */}
                  {(selectedRequest as any).callDuration && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span><strong>Call Duration:</strong> {(selectedRequest as any).callDuration} minutes</span>
                    </div>
                  )}
                </>
              )}

              {/* Medical Certificate */}
              {selectedRequest.needsMedicalCertificate && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <FileText className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm font-medium text-yellow-800">
                      Medical Certificate Required
                    </span>
                  </div>
                </div>
              )}

              {/* Patient Attachments for CURRENT Request */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">📎 Attachments for This Consultation</h4>
                <RequestAttachments requestId={selectedRequest.id || (selectedRequest as any).requestId} />
              </div>

              {/* Full Patient History (Previous Consultations + All Files) */}
              {selectedRequest.patientEmail && (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">📋 Complete Patient History</h4>
                  <PatientConsultationHistory patientEmail={selectedRequest.patientEmail} />
                </div>
              )}
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setSelectedRequest(null)}
                className="flex-1 btn-secondary"
              >
                Close
              </button>
              <button
                onClick={() => {
                  openCompleteModal(selectedRequest);
                }}
                className="flex-1 btn-primary"
              >
                Complete Call
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Call Modal */}
      {showCompleteModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-luma-text mb-4">
              Complete Call - {selectedRequest.patientName}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call Duration (minutes) *
                </label>
                <input
                  type="number"
                  value={callDuration}
                  onChange={(e) => setCallDuration(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent"
                  placeholder="25"
                  min="1"
                  max="120"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <span className="mr-2">🔒</span>
                  Internal Doctor Notes (Private) *
                </label>
                <textarea
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent"
                  placeholder="Clinical observations, diagnosis details, internal notes...

Example:
- Patient presented with [symptoms]
- Physical examination findings: [details]
- Diagnosis: [condition]
- Prescribed: [medications with dosage]"
                  rows={6}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  🔒 Private - These notes are NOT visible to the patient
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <span className="mr-2">📧</span>
                  Patient Recommendation (Sent via Email) *
                </label>
                <textarea
                  value={patientRecommendation}
                  onChange={(e) => setPatientRecommendation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent"
                  placeholder="Treatment plan and recommendations for patient...

Example:
- Take paracetamol 500mg every 6 hours for fever
- Rest and drink plenty of water
- Avoid strenuous activities for 3 days
- Return if symptoms persist beyond 3 days or worsen"
                  rows={6}
                  required
                />
                <p className="text-xs text-luma-primary mt-1">
                  📧 This will be sent to the patient via email
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="mcIssued"
                  checked={mcIssued}
                  onChange={(e) => setMcIssued(e.target.checked)}
                  className="w-4 h-4 text-luma-primary border-gray-300 rounded focus:ring-luma-primary"
                />
                <label htmlFor="mcIssued" className="text-sm text-gray-700">
                  Medical certificate issued
                </label>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowCompleteModal(false)}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCompleteCall}
                disabled={!callNotes.trim() || !patientRecommendation.trim() || callDuration < 1 || actionLoading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
              >
                {actionLoading ? 'Completing...' : '✅ Complete & Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Call Modal */}
      {showCancelModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-luma-text mb-4">
              Cancel Call - {selectedRequest.patientName}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cancellation Reason
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent"
                rows={3}
                placeholder="Please provide a reason for cancellation..."
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={actionLoading}
                className="flex-1 btn-secondary"
              >
                Keep Request
              </button>
              <button
                onClick={handleCancelCall}
                disabled={!cancelReason.trim() || actionLoading}
                className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-600 transition-all"
              >
                {actionLoading ? 'Cancelling...' : 'Cancel Call'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorCallRequests;